import {
  getGetMusicsApiV1QueryKey,
  useFavoriteMusicApiV1,
  useUnfavoriteMusicApiV1,
} from "@/api/__generated__/api.schema.ts";
import { TrackingEvent, trackEvent } from "@/lib/track/track-event";
import { cn } from "@/lib/utils";
import { useToastMessage } from "@backpac/monica";
import { Button, Icon } from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import type { HTMLAttributes } from "react";

interface FavoriteMusicButtonProps extends HTMLAttributes<HTMLButtonElement> {
  assetId: string;
  isFavorite: boolean;
}

export function FavoriteMusicButton({ assetId, isFavorite, className, ...props }: FavoriteMusicButtonProps) {
  const queryClient = useQueryClient();
  const { info } = useToastMessage();
  const { mutate: favoriteMusic } = useFavoriteMusicApiV1({
    mutation: {
      onSuccess: () => {
        info("Added to favorites");
        trackEvent(TrackingEvent.FavoriteMusic, {
          music_id: assetId,
        });
        queryClient.refetchQueries({
          queryKey: [...getGetMusicsApiV1QueryKey()],
        });
      },
    },
  });
  const { mutate: unfavoriteMusic } = useUnfavoriteMusicApiV1({
    mutation: {
      onSuccess: () => {
        info("Removed from favorites");
        trackEvent(TrackingEvent.UnfavoriteMusic, {
          music_id: assetId,
        });
        queryClient.refetchQueries({
          queryKey: [...getGetMusicsApiV1QueryKey()],
        });
      },
    },
  });
  const toggle = isFavorite ? unfavoriteMusic : favoriteMusic;

  return (
    <Button
      variant="ghost"
      className={cn(
        "p-2 rounded-[8px] text-textPrimary",
        "hover:bg-backgroundDark",
        {
          "text-colorRed": isFavorite,
        },
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        toggle({ data: { assetId } });
      }}
      {...props}
    >
      {isFavorite ? <Icon name="heart-fill" className="size-5" /> : <Icon name="heart" className="size-5" />}
    </Button>
  );
}
